import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Menu, X, Users, LogOut, LayoutDashboard, MapPin, Clock } from 'lucide-react';
import { toast } from 'sonner';
import DateRangePicker from './DateRangePicker';
import LateDetailModal from './LateDetailModal';
import VacationDetailModal from './VacationDetailModal';
import AbsentDetailModal from './AbsentDetailModal';
import Spinner from './Spinner';
import PersonalStats from './PersonalStats';
import OvertimeBalanceView from './OvertimeBalanceView';

const Dashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const getTodayKST = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    const [activeTab, setActiveTab] = useState('dashboard');
    const [startDate, setStartDate] = useState(getTodayKST());
    const [endDate, setEndDate] = useState(getTodayKST());

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedLocationOvertime, setSelectedLocationOvertime] = useState(null);
    const [lateCount, setLateCount] = useState(0);
    const [vacationCount, setVacationCount] = useState(0);
    const [memberList, setMemberList] = useState([]);
    const [memberListOvertime, setMemberListOvertime] = useState([]);
    const [isListLoading, setIsListLoading] = useState(false);

    const [locationData, setLocationData] = useState([]);
    const [locationDataOvertime, setLocationDataOvertime] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [isLateModalOpen, setIsLateModalOpen] = useState(false);
    const [lateDetailList, setLateDetailList] = useState([]);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    const [isVacationModalOpen, setIsVacationModalOpen] = useState(false);
    const [vacationDetailList, setVacationDetailList] = useState([]);
    const [isVacationLoading, setIsVacationLoading] = useState(false);

    const [absentCount, setAbsentCount] = useState(0);
    const [isAbsentModalOpen, setIsAbsentModalOpen] = useState(false);
    const [absentDetailList, setAbsentDetailList] = useState([]);
    const [isAbsentLoading, setIsAbsentLoading] = useState(false);

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const detailTableRef = useRef(null);

    const SERVER_BASE = process.env.REACT_APP_SERVER_BASE_URL;
    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];
    const unit = startDate === endDate ? '명' : '번';

    const SummaryButton = ({ onClick, color, label, count, unit, isMobile, isLoading }) => {
        const colors = {
            rose: "bg-rose-50 border-rose-100 text-rose-700",
            orange: "bg-orange-50 border-orange-100 text-orange-700",
            emerald: "bg-emerald-50 border-emerald-100 text-emerald-700"
        };

        return (
            <div 
                onClick={onClick}
                className={`flex items-center justify-center border rounded-xl shadow-sm cursor-pointer hover:scale-[1.02] active:scale-[0.98] transition-all ${colors[color]} ${isMobile ? "flex-col gap-0" : "px-4 gap-4"}`}
            >
                <span className={`${isMobile ? "text-[10px]" : "text-[11px]"} font-bold opacity-70`}>{label}</span>
                {isLoading ? (
                    // 🚀 로딩 중일 때 표시할 스피너 애니메이션
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                    <span className={`${isMobile ? "text-sm" : "text-base"} font-bold`}>{count}{isMobile ? "" : ` ${unit}`}</span>
                )}
            </div>
        );
    };

    const handleOpenAbsentModal = async () => {
        setIsAbsentModalOpen(true);
        setIsAbsentLoading(true);
        try {
            const token = localStorage.getItem('token');
            // 조회 기간은 단일 날짜일 때 가장 정확하므로 startDate 전달
            const response = await fetch(
            `${SERVER_BASE}/stats/absent-details?startDate=${startDate}&endDate=${endDate}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            if (result.success) setAbsentDetailList(result.data);
        } catch (err) {
            console.error("미출근 명단 로딩 실패", err);
        } finally {
            setIsAbsentLoading(false);
        }
    };

    const handleOpenVacationModal = async () => {
        setIsVacationModalOpen(true);
        setIsVacationLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
            `${SERVER_BASE}/stats/vacation-details?startDate=${startDate}&endDate=${endDate}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            if (result.success) setVacationDetailList(result.data);
        } catch (err) {
            console.error("연차 명단 로딩 실패", err);
        } finally {
            setIsVacationLoading(false);
        }
    };
    const handleOpenLateModal = async () => {
        setIsLateModalOpen(true);
        setIsDetailLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
            `${SERVER_BASE}/stats/late-details?startDate=${startDate}&endDate=${endDate}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            if (result.success) setLateDetailList(result.data);
        } catch (err) {
            console.error("지각 명단 로딩 실패", err);
        } finally {
            setIsDetailLoading(false);
        }
    };
    const handleDateChange = (start, end) => {
        // 🚀 핵심 로직: 시작일이 종료일보다 뒤라면, 시작일을 종료일에 맞춤

        let finalStart = start;
        let finalEnd = end;

        if (start > end) {
            finalStart = end; // 시작일을 종료일로 변경
        }

        setStartDate(finalStart);
        setEndDate(finalEnd);
        setSelectedLocation(null);
        setSelectedLocationOvertime(null);
        setMemberList([]);
    };

    const handleCardClick = async (locationName) => {
        // 1. 이미 열려있는 카드를 다시 누르면 닫기 (토글)

        if(startDate !== endDate) {
            toast.info(`상세 근무 내역은 단일기간 조회에서만 지원해요.`);
            return;
        }
        if (selectedLocation === locationName) {
            setSelectedLocation(null);
            setMemberList([]);
            return;
        }

        // 2. 다른 카드 혹은 처음 누르는 경우
        setSelectedLocation(locationName);
        setIsListLoading(true);
        
        setTimeout(() => {
            detailTableRef.current?.scrollIntoView({ 
                behavior: 'smooth', // 부드러운 스크롤
                block: 'start'      // 요소의 시작 지점으로 이동
            });
        }, 100);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${SERVER_BASE}/stats/location-details?locationName=${encodeURIComponent(locationName)}&startDate=${startDate}&endDate=${endDate}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            if (result.success) {
                setMemberList(result.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsListLoading(false);
        }
    };

    const handleOvertimeCardClick = async (locationName) => {
        // 1. 이미 열려있는 카드를 다시 누르면 닫기 (토글)

        if(startDate !== endDate) {
            toast.info(`상세 근무 내역은 단일기간 조회에서만 지원해요.`);
            return;
        }
        if (selectedLocationOvertime === locationName) {
            setSelectedLocationOvertime(null);
            setMemberListOvertime([]);
            return;
        }

        // 2. 다른 카드 혹은 처음 누르는 경우
        setSelectedLocationOvertime(locationName);
        setIsListLoading(true);
        
        setTimeout(() => {
            detailTableRef.current?.scrollIntoView({ 
                behavior: 'smooth', // 부드러운 스크롤
                block: 'start'      // 요소의 시작 지점으로 이동
            });
        }, 100);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${SERVER_BASE}/stats/location-details-overtime?locationName=${encodeURIComponent(locationName)}&startDate=${startDate}&endDate=${endDate}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            if (result.success) {
                setMemberListOvertime(result.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsListLoading(false);
        }
    };

    const handleLogout = useCallback(() => {
        localStorage.clear();
        navigate('/login');
    }, [navigate]);

    const fetchStats = useCallback(async (token) => {
        try {
            setIsLoading(true);
            const urlParams = `?startDate=${startDate}&endDate=${endDate}`;

            const [statsRes, statsOvertimeRes, summaryRes] = await Promise.all([
                fetch(`${SERVER_BASE}/stats/location-counts${urlParams}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${SERVER_BASE}/stats/location-counts-overtime${urlParams}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(`${SERVER_BASE}/stats/attendance-summary${urlParams}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            const result = await statsRes.json();
            if (result.success) {
                const filteredData = result.data.filter(item => item.value > 0);
                setLocationData(filteredData);
            }
            const result_overtime = await statsOvertimeRes.json();
            if (result_overtime.success) {
                const filteredData = result_overtime.data.filter(item => item.value > 0);
                setLocationDataOvertime(filteredData);
            }
            
            const summaryData = await summaryRes.json();
            if (summaryData.success) {
                setLateCount(summaryData.lateCount);
                setVacationCount(summaryData.vacationCount);
                setAbsentCount(summaryData.absentCount || 0);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [SERVER_BASE, startDate, endDate]);

    useEffect(() => {
        const syncUserRole = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            try {
                const response = await fetch(`${SERVER_BASE}/auth/me`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    const result = await response.json();
                    localStorage.setItem('user', JSON.stringify(result.user));
                    localStorage.setItem('token', result.token);
                    setUser(result.user);
                    fetchStats(result.token);
                } else {
                    // 토큰이 유효하지 않거나 유저가 비활성화된 경우
                    handleLogout();
                }
            } catch (err) {
                console.error("Role Sync Error:", err);
            } finally {
                // 권한 정보가 갱신된 후에 통계 데이터를 가져오도록 설계

            }
        };
        syncUserRole();
    }, [navigate, SERVER_BASE, handleLogout, fetchStats]);

    return (
        <div className="min-h-screen bg-slate-50 flex overflow-x-hidden">
            {/* 🚀 모바일 전용 사이드바 오버레이 (사이드바 열렸을 때 배경 어둡게) */}
            {isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-slate-900/50 z-40 md:hidden transition-opacity"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* 사이드바 */}
            <aside className={`
                fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform duration-300 ease-in-out
                md:relative md:translate-x-0 
                ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                flex flex-col
            `}>
                <div className="p-6 text-xl font-bold border-b border-slate-800 flex justify-between items-center">
                    <span>GLIM ERP LINK</span>
                    {/* 🚀 모바일용 닫기 버튼 */}
                    <button onClick={() => setIsSidebarOpen(false)} className="md:hidden text-slate-400">
                        <X size={24} />
                    </button>
                </div>
                <nav className="flex-1 p-4 space-y-2">
                    <button 
                        onClick={() => { setActiveTab('dashboard'); setIsSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                            activeTab === 'dashboard' ? 'bg-blue-600 shadow-lg' : 'text-slate-400 hover:bg-slate-800'
                        }`}
                    >
                        <LayoutDashboard size={20} />
                        <span>Dashboard</span>
                    </button>

                    <button 
                        onClick={() => { setActiveTab('overtime_balance'); setIsSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                            activeTab === 'overtime_balance' ? 'bg-blue-600 shadow-lg text-white' : 'text-slate-400 hover:bg-slate-800'
                        }`}
                    >
                        <Clock size={20} />
                        <span>대체휴무 정보</span>
                    </button>

                    {/* 개인별 통계 버튼 */}
                    <button 
                        onClick={() => { setActiveTab('personal'); setIsSidebarOpen(false); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                            activeTab === 'personal' ? 'bg-blue-600 shadow-lg' : 'text-slate-400 hover:bg-slate-800'
                        }`}
                    >
                        <Users size={20} />
                        <span>개인별 통계</span>
                    </button>
                </nav>
                <button 
                onClick={handleLogout} 
                className="hidden md:flex p-6 items-center gap-2 text-slate-400 hover:text-white transition-colors border-t border-slate-800"
                >
                    <LogOut size={18} /> 로그아웃
                </button>
            </aside>

            {/* 메인 섹션 */}
            <main className="flex-1 min-w-0 p-4">
                {activeTab === 'dashboard' ? (
                    <>
                    <header className="mb-4 flex flex-col gap-4">
                        {/* 데스크톱: 전체를 수평 정렬, 모바일: 수직 정렬 */}
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                            
                            {/* [상단 그룹] 햄버거 버튼 & 버튼들 */}
                            <div className="flex items-center gap-2 w-full md:w-auto">
                                <button 
                                    onClick={() => setIsSidebarOpen(true)}
                                    className="p-2 bg-white rounded-lg shadow-sm border border-slate-200 md:hidden text-slate-600 shrink-0"
                                >
                                    <Menu size={24} />
                                </button>
    
                                {/* 데스크톱용 버튼 그룹 (사이드바 우측에 바로 붙음) */}
                                <div className="hidden md:flex gap-2 h-[40px]">
                                    <SummaryButton onClick={handleOpenAbsentModal} isLoading={isLoading} color="rose" label="미출근" count={absentCount} unit={unit} />
                                    <SummaryButton onClick={handleOpenLateModal} isLoading={isLoading} color="orange" label="지각" count={lateCount} unit={unit} />
                                    <SummaryButton onClick={handleOpenVacationModal} isLoading={isLoading} color="emerald" label="연차" count={vacationCount} unit="명" />
                                </div>
                            </div>
    
                            {/* [날짜 피커] 모바일에서는 전체 폭, 데스크톱에서는 우측 정렬(ml-auto) */}
                            <div className="w-full md:ml-auto md:w-auto">
                                <DateRangePicker startDate={startDate} endDate={endDate} onRangeChange={handleDateChange} />
                            </div>
                        </div>
    
                        {/* [모바일 전용 버튼] md:hidden */}
                        <div className="md:hidden grid grid-cols-3 gap-4 h-[45px] mt-2">
                            <SummaryButton onClick={handleOpenAbsentModal} color="rose" isLoading={isLoading} label="미출근" count={absentCount} unit={unit} isMobile />
                            <SummaryButton onClick={handleOpenLateModal} color="orange" isLoading={isLoading} label="지각" count={lateCount} unit={unit} isMobile />
                            <SummaryButton onClick={handleOpenVacationModal} color="emerald" isLoading={isLoading} label="연차" count={vacationCount} unit="명" isMobile />
                        </div>
    
                        {/* 모달 컴포넌트 배치 */}
                        <AbsentDetailModal 
                            isOpen={isAbsentModalOpen}
                            onClose={() => setIsAbsentModalOpen(false)}
                            startDate={startDate}
                            endDate={endDate}
                            data={absentDetailList}
                            isLoading={isAbsentLoading}
                        />
                        <LateDetailModal 
                            isOpen={isLateModalOpen}
                            onClose={() => setIsLateModalOpen(false)}
                            startDate={startDate}
                            endDate={endDate}
                            data={lateDetailList}
                            isLoading={isDetailLoading}
                        />
                        <VacationDetailModal 
                            isOpen={isVacationModalOpen}
                            onClose={() => setIsVacationModalOpen(false)}
                            startDate={startDate}
                            endDate={endDate}
                            data={vacationDetailList}
                            isLoading={isVacationLoading}
                        />
                    </header>
    
                    {/* 상단 요약 카드 */}
                    {!isLoading && (
                    <>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">
                        일반 근무
                        </h3>
                        {locationData.length !== 0 && (
                            <div className="grid grid-cols-3 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-4 mb-4">
                                {/* $ 표시를 제거하고 중괄호만 사용합니다 */}
                                {locationData.map((item) => (
                                    <div 
                                    key={item.name} 
                                    onClick={() => handleCardClick(item.name)} 
                                    className={`relative overflow-hidden cursor-pointer transition-all p-3 lg:p-6 rounded-2xl border-2 ${
                                        selectedLocation === item.name 
                                        ? 'border-blue-500 bg-blue-50 shadow-inner' 
                                        : 'border-transparent bg-white shadow-sm hover:shadow-md'
                                    }`}
                                    >
                                    <div className="relative z-10">
                                        <p className="text-[10px] lg:text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1 truncate">
                                        {item.name}
                                        </p>
                                        <p className="text-lg lg:text-3xl font-bold text-slate-800">
                                        {item.value}
                                        <span className="text-[10px] lg:text-sm font-normal text-slate-400 ml-0.5">{unit}</span>
                                        </p>
                                    </div>
                                    <MapPin 
                                        className={`absolute -right-1 -bottom-1 w-8 h-8 lg:w-16 lg:h-16 pointer-events-none transition-colors ${
                                        selectedLocation === item.name ? 'text-blue-100' : 'text-slate-50'
                                        }`} 
                                    />
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                    )}
    
                    {selectedLocation && (
                        <div ref={detailTableRef} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4 animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-slate-800">
                                    <span className="text-blue-600">[{selectedLocation}]</span> 상세 근무 내역
                                </h3>
                            </div>
    
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                        <th className="py-4 px-2 text-sm font-semibold text-slate-500 uppercase">이름</th>
                                        <th className="py-4 px-2 text-sm font-semibold text-slate-500 uppercase">팀</th>
                                        <th className="py-4 px-2 text-sm font-semibold text-slate-500 uppercase">출근 시간</th>
                                        </tr>
                                    </thead>
                                    <tbody className={isListLoading ? "opacity-50 transition-opacity" : "opacity-100 transition-opacity"}>
                                        {isListLoading ? (
                                            <div className="h-[400px] w-full flex items-center justify-center">
                                                <div className="w-[30px] h-[30px] flex items-center justify-center gap-[4px] mx-auto">
                                                    {[1, 2, 3, 4, 5].map((_, index) => (
                                                        <div 
                                                            key={index}
                                                            className="w-[6px] h-full animate-random-swing rounded-t-[2px]"
                                                            style={{ 
                                                                backgroundColor: `hsl(${Math.floor(Math.random() * 360)}, 70%, 75%)`,
                                                                animationDelay: `${index * 0.15}s`,
                                                                transformOrigin: 'bottom'
                                                            }} 
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ) : memberList.length > 0 ? (
                                        memberList.map((member, i) => (
                                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                                            <td className="py-4 px-2 font-medium text-slate-700">{member.name}</td>
                                            <td className="py-4 px-2 text-slate-600">{member.team}</td>
                                            <td className="py-4 px-2 text-slate-500 font-mono">{member.attend_time}</td>
                                            </tr>
                                        ))
                                        ) : (
                                        <tr><td colSpan="3" className="py-10 text-center text-slate-400">근무자가 없습니다.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {(isLoading || isListLoading) && (
                        <Spinner/>
                    )}
    
                    {/* 메인 그래프 */}
                    {!isLoading && locationData.length === 0 ? (
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                            <div className="h-[400px] w-full flex flex-col items-center justify-center text-center">
                                {/* 아이콘 영역: 둥근 배경에 부드러운 컬러 부여 */}
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                
                                {/* 서브 텍스트 */}
                                <p className="text-sm text-slate-500">
                                    현재 등록된 근무 정보를 찾을 수 없습니다.<br />
                                    기간 설정을 다시 확인해 주세요.
                                </p>
                            </div>
                        </div>
                    ) : !isLoading && (
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={locationData} margin={{ top: 10, right: 0, left: -30, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                                    <Tooltip 
                                        cursor={{fill: '#f8fafc'}} 
                                        contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}}
                                        formatter={(value) => [`${value}${unit}`]}
                                    />
                                    <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={50}>
                                        {locationData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                    {!isLoading && (
                        <>
                            <h3 className="text-lg font-bold text-slate-800 mt-2 mb-2">
                                    초과 근무
                            </h3>
                            {locationDataOvertime.length !== 0 && (
                                <div className="grid grid-cols-3 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-4 mb-4">
                                    {locationDataOvertime.map((item) => (
                                        <div 
                                            key={item.name} 
                                            onClick={() => handleOvertimeCardClick(item.name)} 
                                            className={`relative overflow-hidden cursor-pointer transition-all p-3 lg:p-6 rounded-2xl border-2 ${
                                            selectedLocationOvertime === item.name 
                                                ? 'border-blue-500 bg-blue-50 shadow-inner' 
                                                : 'border-transparent bg-white shadow-sm hover:shadow-md'
                                            }`}
                                        >
                                            <div className="relative z-10">
                                                {/* 모바일에서는 텍스트 크기를 줄임 (text-[10px] 등) */}
                                                <p className="text-[10px] lg:text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1 truncate">
                                                    {item.name}
                                                </p>
                                                {/* 폰트 크기 조정 */}
                                                <p className="text-lg lg:text-3xl font-bold text-slate-800">
                                                    {item.value}
                                                    <span className="text-[10px] lg:text-sm font-normal text-slate-400 ml-0.5">{unit}</span>
                                                </p>
                                            </div>
                                            {/* 모바일에서는 아이콘을 숨기거나 크기를 대폭 줄임 */}
                                            <MapPin 
                                                className={`absolute -right-1 -bottom-1 w-8 h-8 lg:w-16 lg:h-16 pointer-events-none transition-colors ${
                                                    selectedLocationOvertime === item.name ? 'text-blue-100' : 'text-slate-50'
                                                }`} 
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                    {selectedLocationOvertime && (
                        <div ref={detailTableRef} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 mb-4 animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-300">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-slate-800">
                                    <span className="text-blue-600">[{selectedLocationOvertime}]</span> 상세 근무 내역
                                </h3>
                            </div>
    
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-slate-100">
                                        <th className="py-4 px-2 text-sm font-semibold text-slate-500 uppercase">이름</th>
                                        <th className="py-4 px-2 text-sm font-semibold text-slate-500 uppercase">팀</th>
                                        <th className="py-4 px-2 text-sm font-semibold text-slate-500 uppercase">출근 시간</th>
                                        </tr>
                                    </thead>
                                    <tbody className={isListLoading ? "opacity-50 transition-opacity" : "opacity-100 transition-opacity"}>
                                        {isListLoading ? (
                                            <div className="h-[400px] w-full flex items-center justify-center">
                                                <div className="w-[30px] h-[30px] flex items-center justify-center gap-[4px] mx-auto">
                                                    {[1, 2, 3, 4, 5].map((_, index) => (
                                                        <div 
                                                            key={index}
                                                            className="w-[6px] h-full animate-random-swing rounded-t-[2px]"
                                                            style={{ 
                                                                backgroundColor: `hsl(${Math.floor(Math.random() * 360)}, 70%, 75%)`,
                                                                animationDelay: `${index * 0.15}s`,
                                                                transformOrigin: 'bottom'
                                                            }} 
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        ) : memberListOvertime.length > 0 ? (
                                        memberListOvertime.map((member, i) => (
                                            <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                                            <td className="py-4 px-2 font-medium text-slate-700">{member.name}</td>
                                            <td className="py-4 px-2 text-slate-600">{member.team}</td>
                                            <td className="py-4 px-2 text-slate-500 font-mono">{member.attend_time}</td>
                                            </tr>
                                        ))
                                        ) : (
                                        <tr><td colSpan="3" className="py-10 text-center text-slate-400">근무자가 없습니다.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
    
                    {!isLoading && locationDataOvertime.length !== 0 ? (
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                            <div className="h-[400px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={locationDataOvertime} margin={{ top: 10, right: 0, left: -30, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 12}} />
                                    <Tooltip 
                                        cursor={{fill: '#f8fafc'}} 
                                        contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'}}
                                        formatter={(value) => [`${value}${unit}`]}
                                    />
                                    <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={50}>
                                        {locationDataOvertime.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    ) : !isLoading && (
                        <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
                            <div className="h-[400px] w-full flex flex-col items-center justify-center text-center">
                                {/* 아이콘 영역: 둥근 배경에 부드러운 컬러 부여 */}
                                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </div>
                                
                                {/* 서브 텍스트 */}
                                <p className="text-sm text-slate-500">
                                    현재 등록된 근무 정보를 찾을 수 없습니다.<br />
                                    기간 설정을 다시 확인해 주세요.
                                </p>
                            </div>
                        </div>
                    )}
                    </>
                ) : activeTab === "personal" ? (
                    <PersonalStats setIsSidebarOpen={setIsSidebarOpen} />
                ) : (
                    <OvertimeBalanceView setIsSidebarOpen={setIsSidebarOpen} />
                )}
            </main>
        </div>
    );
};

export default Dashboard;